import moment from 'moment'
import momentShortFormat from 'moment-shortformat'
import './locale'

// Formatting

export const relativeDate = dateString => {
  return moment(dateString).fromNow()
}

export const relativeShort = dateString => {
  return momentShortFormat(dateString).short(true)
}

export const absoluteDate = dateString => {
  return moment(dateString).format('l')
}

export const universalDate = dateString => {
  return moment(dateString).format('D/M/YYYY')
}

export const absoluteDatetime = dateString => {
  return moment(dateString).format('l LT')
}

export const dayOfWeek = (dateString, abbreviated) => {
  const format = abbreviated ? 'ddd' : 'dddd'

  return moment(dateString).format(format)
}

export const dayNumber = dateString => {
  return moment(dateString).format('D')
}

export const monthName = (dateString, abbreviated) => {
  const format = abbreviated ? 'MMM' : 'MMMM'

  return moment(dateString).format(format)
}

export const monthNumber = dateString => {
  return moment(dateString).format('M')
}

export const yearNumber = (dateString, abbreviated) => {
  const format = abbreviated ? 'YY' : 'YYYY'

  return moment(dateString).format(format)
}

export const timeOnly = dateString => {
  return moment(dateString).format('LT')
}

export const isoDateTime = dateString => {
  return moment.utc(dateString).format()
}

export const isoDateOnly = dateString => {
  return moment.utc(dateString).format('YYYY-MM-DD')
}

// Generating

export const getDateSourceValue = (source, isAction = false) => {
  // initialize date as local
  let date = moment()

  const options = source.options || {}

  if (options.startOfDay) {
    date = date.startOf('day')
  }

  if (options.offset) {
    if (options.offset % 1 === 0) {
      date.add(options.offset, 'days')
    } else {
      date.add(options.offset * 24 * 60, 'minutes')
    }
  }

  // convert date to UTC
  if (isAction) {
    date = date.utc()
  }

  return serializeDate(date)
}

/**
 *
 * @param {*} date
 * @returns {string | 'Invalid date'}
 */
export const serializeDate = date => {
  return moment(date).format()
}

/**
 *
 * @param {*} date
 * @returns {string | 'Invalid date'}
 */
export const getMostSuitableDateFromInput = date => {
  const attempt1 = +new Date(date)
  if (
    !Number.isNaN(attempt1) &&
    typeof attempt1 === 'number' &&
    attempt1 !== 'Invalid date'
  ) {
    return moment(attempt1).format()
  }

  // Best case is that the date is already in ISO format
  const attempt2 = serializeDate(date)
  if (attempt2 !== 'Invalid date') {
    return attempt2
  }

  const strict = false

  // Next best case is that the date is in the format MM-DD-YYYY
  const attempt3 = moment(new Date(date), 'MM-DD-YYYY', strict).format()
  if (attempt3 !== 'Invalid date') {
    return attempt3
  }

  // If all else fails, try attempt3 again however without passing a Date object
  return moment(date, 'MM-DD-YYYY', strict).format()
}

/**
 *
 * @param {string} date
 * @param {string} start
 * @param {string} end
 * @returns {boolean}
 */
export const isBetweenDateOnly = (date, start, end) => {
  // Reference:
  // https://momentjs.com/docs/#/query/is-between/

  /** @type {moment.unitOfTime.StartOf} */
  const granularity = 'day'

  // date >= start && date < end
  // This is the same logic in utils/conditions.js for non-date values
  const inclusivity = '[)'

  // check if start and end are in date order and reverse them if not
  if (moment(start).isAfter(end)) {
    const temp = start
    start = end
    end = temp
  }

  return moment(date).isBetween(start, end, granularity, inclusivity)
}

/**
 *
 * @param {string} date
 * @param {string} start
 * @param {string} end
 * @returns {boolean}
 */
export const isBetween = (date, start, end) => {
  // Reference:
  // https://momentjs.com/docs/#/query/is-between/

  /** @type {moment.unitOfTime.StartOf} */
  const granularity = 'minute'

  // date >= start && date < end
  // This is the same logic in utils/conditions.js for non-date values
  const inclusivity = '[)'

  // check if start and end are in date order and reverse them if not
  if (moment(start).isAfter(end)) {
    const temp = start
    start = end
    end = temp
  }

  return moment(date).isBetween(start, end, granularity, inclusivity)
}

/**
 *
 * @param {string} date
 * @param {string} otherDate
 * @returns {boolean}
 */
export const isBeforeDateOnly = (date, otherDate) => {
  // Reference:
  // https://momentjs.com/docs/#/query/is-before/

  /** @type {moment.unitOfTime.StartOf} */
  const granularity = 'day'

  return moment(date).isBefore(otherDate, granularity)
}

/**
 *
 * @param {string} date
 * @param {string} otherDate
 * @returns {boolean}
 */
export const isAfterDateOnly = (date, otherDate) => {
  // Reference:
  // https://momentjs.com/docs/#/query/is-after/

  /** @type {moment.unitOfTime.StartOf} */
  const granularity = 'day'

  return moment(date).isAfter(otherDate, granularity)
}

/**
 *
 * @param {string} date
 * @param {string} otherDate
 * @returns {boolean}
 */
export const isSameDateOnly = (date, otherDate) => {
  // Reference:
  // https://momentjs.com/docs/#/query/is-same/

  /** @type {moment.unitOfTime.StartOf} */
  const granularity = 'day'

  return moment(date).isSame(otherDate, granularity)
}

/**
 *
 * @param {string} date
 * @param {string} otherDate
 * @returns {boolean}
 */
export const isBefore = (date, otherDate) => {
  // Reference:
  // https://momentjs.com/docs/#/query/is-before/

  /** @type {moment.unitOfTime.StartOf} */
  const granularity = 'minute'

  return moment(date).isBefore(otherDate, granularity)
}

/**
 *
 * @param {string} date
 * @param {string} otherDate
 * @returns {boolean}
 */
export const isAfter = (date, otherDate) => {
  // Reference:
  // https://momentjs.com/docs/#/query/is-after/

  /** @type {moment.unitOfTime.StartOf} */
  const granularity = 'minute'

  return moment(date).isAfter(otherDate, granularity)
}

/**
 *
 * @param {string} date
 * @param {string} otherDate
 * @returns {boolean}
 */
export const isSame = (date, otherDate) => {
  // Reference:
  // https://momentjs.com/docs/#/query/is-same/

  /** @type {moment.unitOfTime.StartOf} */
  const granularity = 'minute'

  return moment(date).isSame(otherDate, granularity)
}

/**
 *
 * @param {import('moment').Moment} moment
 * @returns {import('moment').Moment}
 */
export const setMomentToDayGranularity = moment => {
  return moment.startOf('day')
}

/**
 *
 * @param {import('moment').Moment} moment
 * @returns {import('moment').Moment}
 */
export const setMomentToMinuteGranularity = moment => {
  return moment.seconds(0)
}
